// src/components/ProtectedRoute.tsx
import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { authFetch } from "../lib/auth";

type Props = { children: ReactNode };

type UserLike = {
  id: number;
  ekyc_status?: string | null;
  account_status?: string | null;
  is_social?: boolean;
  social_provider?: string | null;
  social_id?: string | null;
};

function computeDone(u: UserLike | null | undefined) {
  if (!u) {
    return { isSocial: false, ekycOk: false, accountOk: false, ok: false };
  }

  const isSocial = !!u.is_social || !!u.social_provider || !!u.social_id;
  const ekycOk = u.ekyc_status === "verified";
  const accountOk = u.account_status === "verified";

  // 소셜은 둘 중 하나만, 일반은 둘 다 필요
  const ok = isSocial ? ekycOk || accountOk : ekycOk && accountOk;

  return { isSocial, ekycOk, accountOk, ok };
}

export default function ProtectedRoute({ children }: Props) {
  const { user, ready } = useAuth();
  const location = useLocation();

  const [hasVerification, setHasVerification] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(false);

  const path = location.pathname;

  // ✅ 온보딩/인증 관련 페이지 + 결제등록(/contracts/new) 에서는
  //    온보딩(본인인증) 재검사를 스킵한다.
  const skipOnboardingCheckPrefixes = [
    "/onboarding",
    "/ekyc/reverify",
    "/account/verify",
    "/contracts/new", // ← 여기 추가
  ];
  const shouldSkipOnboardingCheck = skipOnboardingCheckPrefixes.some((p) =>
    path.startsWith(p)
  );

  // ── 모든 훅은 여기 위에서만 사용 ──
  useEffect(() => {
    let cancelled = false;

    const first = computeDone(user as UserLike);
    console.log("[ProtectedRoute] base decision =", first);

    // 온보딩 검사 스킵 페이지면 바로 통과 처리
    if (shouldSkipOnboardingCheck) {
      setHasVerification(true);
      setChecking(false);
      return;
    }

    // user 없으면 굳이 /api/me 호출 안 함
    if (!user) {
      setHasVerification(false);
      setChecking(false);
      return;
    }

    // 이미 조건 만족이면 추가 조회 불필요
    if (first.ok) {
      setHasVerification(true);
      return;
    }

    const check = async () => {
      setChecking(true);
      try {
        let final = first;

        try {
          const res = await authFetch("/api/me");
          if (res.ok) {
            const j: any = await res.json().catch(() => ({}));
            const me: UserLike = j?.user ?? j ?? {};
            final = computeDone(me);
            console.log("[ProtectedRoute] /api/me decision =", final);
          } else {
            console.warn(
              "[ProtectedRoute] /api/me HTTP",
              res.status,
              await res.text().catch(() => "")
            );
          }
        } catch (e) {
          console.warn("[ProtectedRoute] /api/me error", e);
        }

        if (!cancelled) {
          setHasVerification(final.ok);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    };

    check();

    return () => {
      cancelled = true;
    };
  }, [user?.id, shouldSkipOnboardingCheck]);

  // ── 여기부터는 훅 호출 없음 ──

  // AuthProvider 로딩 중이면 일단 아무것도 렌더 X
  if (!ready) return null;

  // 로그인 안 되어 있으면 로그인 페이지로
  if (!user) {
    const next = location.pathname + location.search + location.hash;
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(next)}`}
        replace
        state={{ next }}
      />
    );
  }

  // 온보딩/인증 스킵 경로면 바로 통과 (로그인만 되어 있으면 OK)
  if (shouldSkipOnboardingCheck) {
    return <>{children}</>;
  }

  // 아직 검증 상태 계산 중이면 잠시 빈 화면
  if (checking || hasVerification === null) {
    return null;
  }

  // 최종적으로 본인인증/계좌인증이 조건 미충족이면 온보딩으로 보냄
  if (!hasVerification) {
    const next = location.pathname + location.search + location.hash;
    return (
      <Navigate
        to={`/onboarding?next=${encodeURIComponent(next)}`}
        replace
        state={{ next }}
      />
    );
  }

  // 통과!
  return <>{children}</>;
}
